import {SECTIONS, tagVideo} from "./taxonomy";

// A topic only surfaces as its own sub-playlist when it has at least this many
// videos; smaller topics roll up into the section's flat "More" list. (Per the
// product rule in CATALOG-API-PLAYLISTS-ASK.md — the data tags every video
// regardless; the threshold is a UI concern.)
export const PLAYLIST_THRESHOLD = 3;

// order_in_playlist as a number, or null when unset. Number(null) and
// Number("") are 0, which would mark untagged rows as ordered and skip the
// title fallback, so blank values are treated as missing. A real 0 is kept.
const toOrder = (value) => {
  if (value == null || (typeof value === "string" && !value.trim())) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const naturalCompare = (a, b) =>
  a.localeCompare(b, undefined, {numeric: true, sensitivity: "base"});

// Untagged rows have no order_in_playlist, so fall back to the title. Real
// catalog titles put the episode name first and the series last, in a few
// styles (QA 10-08 catalog):
//   "Alan Turing: Crash Course Computer Science #15"
//   "...: Alternation of Generations - Crash Course Biology #36"
//   "Animal Behavior - CrashCourse Biology #25"
//   "Crash Course Anatomy & Physiology Preview"
// The series is the text after the last "Crash Course" before "#N" or
// "Preview"; titles without it use the text after the last colon. Taking the
// whole prefix made every episode its own series, so playlists sorted A-Z.
// Titles with neither "#N" nor "Preview" are their own series.
const SEPARATORS = /^[\s:|\-\u2013\u2014]+|[\s:|\-\u2013\u2014]+$/g;
const seriesName = (prefix) => {
  const crashCourse = prefix.match(/.*crash\s*course\s+(.+)$/i);
  if (crashCourse) return crashCourse[1].replace(SEPARATORS, "");
  const afterColon = prefix.includes(":")
    ? prefix.slice(prefix.lastIndexOf(":") + 1)
    : prefix;
  return afterColon.replace(SEPARATORS, "");
};

// Exported for unit tests.
export const seriesKey = (title) => {
  const t = String(title || "").trim();
  const episode = t.match(/^(.*?)\s*#\s*(\d+)/);
  if (episode) {
    return {series: seriesName(episode[1]), episode: Number(episode[2])};
  }
  const preview = t.match(/^(.*?)\s*[-:\u2013\u2014]?\s*\bpreview\b/i);
  if (preview && preview[1]) {
    return {series: seriesName(preview[1]), episode: 0};
  }
  return {series: t, episode: null};
};

// Order by series, then episode (preview first), then title as a tiebreak.
// A series is a run of 2+ numbered/preview videos with the same series name.
// Series come first (A-Z, each Preview then #1, #2, ...); single videos,
// including a lone episode like "Crash Course Astronomy #40", follow in their
// incoming (id) order, so a one-off never sits at the top of the list (QA
// 10-09). Sorting singles A-Z broke lesson order for un-numbered playlists
// like Math, mixing AP Calculus and Multivariable Calculus (QA 10-10).
const compareByTitle = (a, b) => {
  const ka = a.key;
  const kb = b.key;
  if (a.inSeries !== b.inSeries) return a.inSeries ? -1 : 1;
  if (!a.inSeries) return a.i - b.i;
  const bySeries = naturalCompare(ka.series, kb.series);
  if (bySeries) return bySeries;
  if (ka.episode !== kb.episode) {
    if (ka.episode == null) return 1;
    if (kb.episode == null) return -1;
    return ka.episode - kb.episode;
  }
  return naturalCompare(a.video.title || "", b.video.title || "") || a.i - b.i;
};

// Group a flat video list into Section -> Playlist -> ordered videos.
//
// Returns:
//   {
//     intro:      [video],            // is_intro, pinned above everything
//     sections:   [{                  // only sections that have content
//       id, title, icon, order,
//       playlists: [{id, title, videos: [video]}],  // >= threshold, title-sorted
//       looseVideos: [video],         // topics under threshold, flattened
//     }],
//     untagged:   [video],            // no section/playlist resolved
//   }
export const buildCatalog = (videos, {threshold = PLAYLIST_THRESHOLD} = {}) => {
  const intro = [];
  const untagged = [];
  // section_id -> {playlists: Map<playlist_id, {id, title, items:[{video, order}]}>}
  const bySection = new Map();

  for (const video of videos || []) {
    const tag = tagVideo(video);

    if (tag.is_intro) {
      intro.push(video);
      continue;
    }
    if (!tag.section_id || !tag.playlist_id) {
      untagged.push(video);
      continue;
    }

    if (!bySection.has(tag.section_id)) {
      bySection.set(tag.section_id, {playlists: new Map()});
    }
    const section = bySection.get(tag.section_id);
    if (!section.playlists.has(tag.playlist_id)) {
      section.playlists.set(tag.playlist_id, {
        id: tag.playlist_id,
        title: tag.playlist_title || tag.playlist_id,
        items: [],
      });
    }
    section.playlists.get(tag.playlist_id).items.push({
      video,
      order: toOrder(tag.order_in_playlist),
    });
  }

  const sections = SECTIONS.filter((s) => bySection.has(s.id))
    .map((s) => {
      const raw = bySection.get(s.id);
      const playlists = [];
      const looseVideos = [];

      for (const p of raw.playlists.values()) {
        // Order videos by order_in_playlist; rows without one go after the
        // ordered rows, sorted by series and episode parsed from the title
        // (the incoming order is the fetch's id sort, which mixes series).
        const keyed = p.items.map((it, i) => ({
          ...it,
          i,
          key: seriesKey(it.video.title),
        }));
        const seriesSize = new Map();
        for (const it of keyed) {
          if (it.key.episode == null) continue;
          const name = it.key.series.toLowerCase();
          seriesSize.set(name, (seriesSize.get(name) || 0) + 1);
        }
        for (const it of keyed) {
          it.inSeries =
            it.key.episode != null &&
            seriesSize.get(it.key.series.toLowerCase()) >= 2;
        }
        const ordered = keyed
          .sort((a, b) => {
            if (a.order == null && b.order == null) return compareByTitle(a, b);
            if (a.order == null) return 1;
            if (b.order == null) return -1;
            return a.order - b.order;
          })
          .map((it) => it.video);

        if (ordered.length >= threshold) {
          playlists.push({id: p.id, title: p.title, videos: ordered});
        } else {
          looseVideos.push(...ordered);
        }
      }

      playlists.sort((a, b) => a.title.localeCompare(b.title));
      return {...s, playlists, looseVideos};
    })
    // A section with no qualifying playlists AND no loose videos shouldn't show.
    .filter((s) => s.playlists.length > 0 || s.looseVideos.length > 0);

  return {intro, sections, untagged};
};
